/**
 * Theme Provider Component
 *
 * @module shared/providers/ThemeProvider
 *
 * Provides MUI theme context with dark/light mode support.
 * Persists theme preference to localStorage.
 */

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { darkTheme, lightTheme } from '@/shared/theme/theme';

import type { ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
  isDark: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'autocollect-theme-mode';
const DEFAULT_MODE: ThemeMode = 'dark';

// ============================================================================
// Context
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access theme context
 *
 * @example
 * ```typescript
 * const { mode, toggleTheme, isDark } = useTheme();
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}

// ============================================================================
// Provider Component
// ============================================================================

interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
}

/**
 * Theme Provider Component
 *
 * Wraps the application with MUI ThemeProvider and provides
 * theme switching functionality.
 *
 * @example
 * ```tsx
 * <ThemeProvider defaultMode="dark">
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({ children, defaultMode = DEFAULT_MODE }: ThemeProviderProps) {
  // Initialize from localStorage or default
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return defaultMode;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }

    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }

    return defaultMode;
  });

  // Persist theme changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);

    // Update document attribute for CSS custom properties
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  // Theme setters
  const setTheme = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
  }, []);

  const toggleTheme = useCallback(() => {
    setModeState(current => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  // Memoized theme object
  const theme = useMemo(() => (mode === 'dark' ? darkTheme : lightTheme), [mode]);

  // Memoized context value
  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      mode,
      toggleTheme,
      setTheme,
      isDark: mode === 'dark',
    }),
    [mode, toggleTheme, setTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

